import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useTranslation } from '../i18n/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { API_BASE_URL } from '../services/api';
import MapLocationPicker from '../components/MapLocationPicker';
import { HeaderBar } from '../components/ui';
import { motion } from 'framer-motion';
import './CreateEvent.css';

function CreateEvent() {
  const [formData, setFormData] = useState({
    eventName: '',
    eventDate: '',
    eventTime: '',
    eventEndTime: '',
    eventLocation: '',
    eventLat: null,
    eventLng: null,
    isPrivate: false,
    accessCode: '',
    eventImage: null,
    eventTheme: '',
    eventVibe: '',
    audienceSize: '',
    eventShortDescription: '',
    eventNotes: '',
    isFeatured: false,
  });
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);

  const totalSteps = 3;
  const canGoNext = () => {
    if (step === 1) return formData.eventName?.trim() && formData.eventDate && formData.eventTime;
    if (step === 2) return formData.eventLocation?.trim();
    return true;
  };
  const goNext = () => { setError(''); if (step < totalSteps) setStep(s => s + 1); };
  const goBack = () => { setError(''); if (step > 1) setStep(s => s - 1); };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError(t('createEvent.imageTooLarge') || 'Image size too large. Maximum 2MB allowed.');
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(t('createEvent.invalidImageType') || 'Please select a valid image file.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({ ...prev, eventImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, eventImage: null }));
  };
  const [createdEvent, setCreatedEvent] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, isAuthenticated, authData, authLoading } = useApp();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const dateLocale = language === 'ar' ? 'ar-EG' : 'en-GB';
  const isDemonstrations = location.pathname.startsWith('/dems');

  const eventThemeIcons = {
    concert_festival: '🎵',
    demonstration: '🪧',
    party_birthday: '🎉',
    conference_workshop: '🏫',
    sports: '⚽',
    wedding: '👰',
    other: '📌',
  };
  const eventThemeLabelKeys = {
    concert_festival: 'eventTypeConcert',
    demonstration: 'eventTypeDemonstration',
    party_birthday: 'eventTypeParty',
    conference_workshop: 'eventTypeConference',
    sports: 'eventTypeSports',
    wedding: 'eventTypeWedding',
    other: 'eventTypeOther',
  };
  const eventVibeLabelKeys = {
    fun_social: 'eventVibeFun',
    chill: 'eventVibeChill',
    high_energy: 'eventVibeHighEnergy',
    calm: 'eventVibeCalm',
    purpose_driven: 'eventVibePurpose',
  };
  const audienceLabelKeys = { small: 'audienceSmall', medium: 'audienceMedium', large: 'audienceLarge' };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      showToast(t('createEvent.pleaseLogin'), 'info');
      const returnTo = isDemonstrations ? '/dems/create-event' : '/create-event';
      const loginPath = isDemonstrations ? '/dems/login' : '/login';
      navigate(loginPath, { state: { returnTo } });
    }
  }, [isAuthenticated, authLoading, navigate, showToast, t, isDemonstrations]);

  // Ensure isPrivate is false for demonstrations
  useEffect(() => {
    if (isDemonstrations && formData.isPrivate) {
      setFormData(prev => ({ ...prev, isPrivate: false, accessCode: '' }));
    }
  }, [isDemonstrations, formData.isPrivate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.eventName || !formData.eventDate || !formData.eventTime || !formData.eventLocation) {
      setError(t('createEvent.fillAllFields'));
      return;
    }

    if (formData.isPrivate && !formData.accessCode) {
      setError(t('createEvent.setAccessCode'));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError(t('createEvent.pleaseLogin'));
        showToast(t('createEvent.pleaseLogin'), 'error');
        navigate('/login', { state: { returnTo: '/create-event' } });
        return;
      }
      
      // Prepare event data
      // For demonstrations, always force public (isPrivate: false)
      const eventPayload = {
        ...formData,
        eventImage: formData.eventImage || null,
        eventType: isDemonstrations ? 'demonstrations' : 'regular',
        isPrivate: isDemonstrations ? false : formData.isPrivate,
        eventTheme: formData.eventTheme || null,
        eventVibe: formData.eventVibe || null,
        audienceSize: formData.audienceSize || null,
        eventShortDescription: formData.eventShortDescription || null,
        eventNotes: formData.eventNotes || null,
        isFeatured: formData.isFeatured || false,
      };
      
      console.log('=== SENDING EVENT PAYLOAD ===');
      console.log('eventTheme:', eventPayload.eventTheme);
      console.log('eventVibe:', eventPayload.eventVibe);
      console.log('audienceSize:', eventPayload.audienceSize);
      console.log('eventShortDescription:', eventPayload.eventShortDescription);
      console.log('isFeatured:', eventPayload.isFeatured);
      
      const response = await fetch(`${API_BASE_URL}/event`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventPayload),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          setError(t('createEvent.sessionExpired'));
          showToast(t('createEvent.sessionExpired'), 'error');
          navigate('/login', { state: { returnTo: '/create-event' } });
          return;
        }
        setError(data.message || t('createEvent.failedToCreate'));
        return;
      }
      
      if (data.success) {
        const eventPath = isDemonstrations ? `/dems/event/${data.event_code}` : `/event/${data.event_code}`;
        const fullLink = `${window.location.origin}${eventPath}`;
        setCreatedEvent({
          ...data,
          fullLink,
          eventName: formData.eventName,
          eventDate: formData.eventDate,
          eventTime: formData.eventTime,
          eventEndTime: formData.eventEndTime,
          eventLocation: formData.eventLocation,
          isPrivate: formData.isPrivate,
          accessCode: formData.accessCode,
        });
        showToast(t('createEvent.createdSuccessfully'), 'success');
      } else {
        setError(data.message || t('createEvent.failedToCreate'));
      }
    } catch (err) {
      setError(t('createEvent.tryAgain'));
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(createdEvent.fullLink);
    showToast(t('createEvent.linkCopied'), 'success');
  };

  const goToEvent = () => {
    const eventPath = isDemonstrations 
      ? `/dems/event/${createdEvent.event_code}`
      : `/event/${createdEvent.event_code}`;
    navigate(eventPath);
  };

  const goToAdminDashboard = () => {
    const adminPath = isDemonstrations
      ? `/dems/event/${createdEvent.event_code}/admin`
      : `/event/${createdEvent.event_code}/admin`;
    navigate(adminPath);
  };

  // Loading state
  if (authLoading || (!isAuthenticated && !authLoading)) {
    return (
      <div className="create-event-page">
        <HeaderBar />
        <div className="create-event-main">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{!isAuthenticated ? t('createEvent.redirectingToLogin') : t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Success screen
  if (createdEvent) {
    return (
      <div className="create-event-page">
        <HeaderBar />
        <div className="create-event-main">
          <motion.div 
            className="create-event-card success-card"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="success-icon">🎉</div>
            <h1>{isDemonstrations ? t('demonstrations.created') : t('createEvent.eventCreated')}</h1>
            <p className="success-subtitle">{isDemonstrations ? t('demonstrations.shareLink') : t('createEvent.shareLink')}</p>
            
            <div className="event-summary">
              <div className="summary-item">
                <span className="summary-label">📅 {isDemonstrations ? t('demonstrations.demonstration') : t('createEvent.event')}</span>
                <span className="summary-value">{createdEvent.eventName}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">🕐 {t('createEvent.when')}</span>
                <span className="summary-value">
                  {new Date(createdEvent.eventDate).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })} {t('createEvent.at')} {createdEvent.eventTime}{createdEvent.eventEndTime && ` - ${createdEvent.eventEndTime}`}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">📍 {t('createEvent.where')}</span>
                <span className="summary-value">{createdEvent.eventLocation}</span>
              </div>
              {createdEvent.isPrivate && (
                <div className="summary-item">
                  <span className="summary-label">🔒 {t('createEvent.accessCode')}</span>
                  <span className="summary-value access-code">{createdEvent.accessCode}</span>
                </div>
              )}
            </div>

            <div className="share-link-box">
              <label>🔗 {t('createEvent.shareThisLink')}</label>
              <div className="share-link-row">
                <input type="text" value={createdEvent.fullLink} readOnly className="share-input" />
                <button onClick={copyLink} className="copy-btn">📋 {t('createEvent.copy')}</button>
              </div>
            </div>

            <div className="success-actions">
              <button onClick={goToAdminDashboard} className="btn-primary">
                ⚙️ {isDemonstrations ? t('demonstrations.manage') : t('createEvent.manageEvent')}
              </button>
              <button onClick={goToEvent} className="btn-secondary">
                🚗 {isDemonstrations ? t('demonstrations.goToPage') : t('createEvent.goToEventPage')} →
              </button>
              <button onClick={() => setCreatedEvent(null)} className="btn-ghost">
                {isDemonstrations ? t('demonstrations.createAnother') : t('createEvent.createAnother')}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-event-page">
      <HeaderBar />
      
      <div className="create-event-main">
        <div className="create-event-layout">
          {/* Left: Live preview */}
          <aside className="create-event-preview-column">
            <div className="create-event-preview-card">
              <div className="create-event-preview-label">{t('createEvent.previewLabel')}</div>
              <div className="create-event-preview-inner">
                {formData.eventImage || imagePreview ? (
                  <div className="create-event-preview-image" style={{ backgroundImage: `url(${formData.eventImage || imagePreview})` }} />
                ) : (
                  <div className="create-event-preview-image-placeholder">
                    <span className="preview-placeholder-icon">📅</span>
                  </div>
                )}
                <div className="create-event-preview-body">
                  <div className="create-event-preview-badges">
                    {formData.eventTheme && eventThemeLabelKeys[formData.eventTheme] && (
                      <span className="preview-badge preview-badge-type">
                        {eventThemeIcons[formData.eventTheme] || '📌'} {t('createEvent.' + eventThemeLabelKeys[formData.eventTheme])}
                      </span>
                    )}
                    {formData.isFeatured && <span className="preview-badge preview-badge-featured">⭐</span>}
                  </div>
                  <h3 className="create-event-preview-title">
                    {formData.eventName?.trim() || t('createEvent.previewPlaceholder')}
                  </h3>
                  {formData.eventShortDescription?.trim() && (
                    <p className="create-event-preview-desc">{formData.eventShortDescription}</p>
                  )}
                  {(formData.eventDate || formData.eventTime) && (
                    <p className="create-event-preview-meta">
                      📅 {formData.eventDate && new Date(formData.eventDate + 'T12:00:00').toLocaleDateString(dateLocale, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      {formData.eventTime && ` · ${formData.eventTime}${formData.eventEndTime ? ` – ${formData.eventEndTime}` : ''}`}
                    </p>
                  )}
                  {formData.eventLocation?.trim() && (
                    <p className="create-event-preview-location">📍 {formData.eventLocation}</p>
                  )}
                  {(formData.eventVibe || formData.audienceSize) && (
                    <div className="create-event-preview-tags">
                      {formData.eventVibe && eventVibeLabelKeys[formData.eventVibe] && <span className="preview-tag">{t('createEvent.' + eventVibeLabelKeys[formData.eventVibe])}</span>}
                      {formData.audienceSize && audienceLabelKeys[formData.audienceSize] && <span className="preview-tag">{t('createEvent.' + audienceLabelKeys[formData.audienceSize])}</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>

          {/* Right: Form */}
          <div className="create-event-form-column">
        <motion.div 
          className="create-event-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className="create-event-header">
            <h1>{isDemonstrations ? t('demonstrations.createTitle') : t('createEvent.title')}</h1>
            <p>{isDemonstrations ? t('demonstrations.createSubtitle') : t('createEvent.subtitle')}</p>
            
            <div className="creator-badge">
              <div className="creator-avatar">
                {authData?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="creator-info">
                <span className="creator-name">{t('createEvent.creatingAs')} {authData?.name}</span>
                <span className="creator-role">{t('createEvent.youWillBeAdmin')}</span>
              </div>
            </div>
          </div>
          
          {/* Step indicator */}
          <div className="create-event-steps">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <button
                  type="button"
                  className={`step-dot ${step === s ? 'active' : ''} ${step > s ? 'done' : ''}`}
                  onClick={() => step > s && setStep(s)}
                  aria-label={`Step ${s}`}
                >
                  {step > s ? '✓' : s}
                </button>
                {s < 3 && <span className="step-connector" />}
              </React.Fragment>
            ))}
          </div>
          <p className="step-title">{t('createEvent.step' + step + 'Title')}</p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="create-event-form">
            {/* Step 1: What & when */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                className="form-step"
              >
                <div className="form-group">
                  <label>{isDemonstrations ? t('demonstrations.nameLabel') : t('createEvent.eventName')} *</label>
                  <input
                    type="text"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleInputChange}
                    placeholder={isDemonstrations ? t('demonstrations.namePlaceholder') : t('createEvent.eventNamePlaceholder')}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>{t('createEvent.eventType')}</label>
                  <select name="eventTheme" value={formData.eventTheme} onChange={handleInputChange} className="form-input">
                    <option value="">{t('createEvent.eventTypePlaceholder')}</option>
                    <option value="concert_festival">{t('createEvent.eventTypeConcert')}</option>
                    <option value="demonstration">{t('createEvent.eventTypeDemonstration')}</option>
                    <option value="party_birthday">{t('createEvent.eventTypeParty')}</option>
                    <option value="conference_workshop">{t('createEvent.eventTypeConference')}</option>
                    <option value="sports">{t('createEvent.eventTypeSports')}</option>
                    <option value="wedding">{t('createEvent.eventTypeWedding')}</option>
                    <option value="other">{t('createEvent.eventTypeOther')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('createEvent.eventVibe')}</label>
                  <div className="vibe-chips">
                    {[
                      { value: 'fun_social', label: t('createEvent.eventVibeFun') },
                      { value: 'chill', label: t('createEvent.eventVibeChill') },
                      { value: 'high_energy', label: t('createEvent.eventVibeHighEnergy') },
                      { value: 'calm', label: t('createEvent.eventVibeCalm') },
                      { value: 'purpose_driven', label: t('createEvent.eventVibePurpose') },
                    ].map((opt) => (
                      <label key={opt.value} className={`vibe-chip ${formData.eventVibe === opt.value ? 'selected' : ''}`}>
                        <input type="radio" name="eventVibe" value={opt.value} checked={formData.eventVibe === opt.value} onChange={handleInputChange} />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('createEvent.audienceSize')}</label>
                  <div className="audience-options">
                    {[
                      { value: 'small', label: t('createEvent.audienceSmall') },
                      { value: 'medium', label: t('createEvent.audienceMedium') },
                      { value: 'large', label: t('createEvent.audienceLarge') },
                    ].map((opt) => (
                      <label key={opt.value} className={`audience-option ${formData.audienceSize === opt.value ? 'selected' : ''}`}>
                        <input type="radio" name="audienceSize" value={opt.value} checked={formData.audienceSize === opt.value} onChange={handleInputChange} />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('createEvent.eventShortDescription')}</label>
                  <input type="text" name="eventShortDescription" value={formData.eventShortDescription} onChange={handleInputChange} placeholder={t('createEvent.eventShortDescriptionPlaceholder')} className="form-input" maxLength={200} />
                </div>
                <div className="form-group">
                  <label>{t('createEvent.eventNotes')} <span className="optional-label">({t('common.optional')})</span></label>
                  <input type="text" name="eventNotes" value={formData.eventNotes} onChange={handleInputChange} placeholder={t('createEvent.eventNotesPlaceholder')} className="form-input" maxLength={200} />
                </div>
                <div className="form-group">
                  <label className="featured-toggle">
                    <input type="checkbox" name="isFeatured" checked={formData.isFeatured} onChange={handleInputChange} />
                    <span className="toggle-track"><span className="toggle-thumb" /></span>
                    <span className="toggle-content">
                      <span className="toggle-title">⭐ {t('createEvent.isFeatured')}</span>
                      <span className="toggle-desc">{t('createEvent.isFeaturedHint')}</span>
                    </span>
                  </label>
                </div>
                <div className="form-group">
                  <label>{t('createEvent.eventImage')} <span className="optional-label">({t('common.optional')})</span></label>
                  <div className="image-upload-container">
                    {imagePreview ? (
                      <div className="image-preview-wrapper">
                        <img src={imagePreview} alt="" className="image-preview" />
                        <button type="button" className="remove-image-btn" onClick={removeImage} aria-label="Remove">✕</button>
                      </div>
                    ) : (
                      <label className="image-upload-box">
                        <input type="file" accept="image/*" onChange={handleImageChange} className="image-input" />
                        <div className="upload-placeholder">
                          <span className="upload-icon">📷</span>
                          <span className="upload-text">{t('createEvent.uploadImage')}</span>
                          <span className="upload-hint">{t('createEvent.imageHint')}</span>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('createEvent.date')} *</label>
                  <input type="date" name="eventDate" value={formData.eventDate} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]} className="form-input" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('createEvent.startTime')} *</label>
                    <input type="time" name="eventTime" value={formData.eventTime} onChange={handleInputChange} className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>{t('createEvent.endTime')}</label>
                    <input type="time" name="eventEndTime" value={formData.eventEndTime} onChange={handleInputChange} className="form-input" />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Where */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                className="form-step"
              >
                <div className="form-group">
                  <label>{isDemonstrations ? t('demonstrations.locationLabel') : t('createEvent.eventLocation')} *</label>
                  <MapLocationPicker
                    value={formData.eventLocation}
                    onChange={(value) => setFormData(prev => ({ ...prev, eventLocation: value }))}
                    onLocationSelect={(loc) => setFormData(prev => ({ ...prev, eventLocation: loc.address, eventLat: loc.lat, eventLng: loc.lng }))}
                    placeholder={isDemonstrations ? t('demonstrations.searchLocation') : t('createEvent.searchLocation')}
                  />
                </div>
              </motion.div>
            )}

            {/* Step 3: Who can join */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                className="form-step"
              >
                {!isDemonstrations && (
                  <>
                    <div className="privacy-toggle-wrapper">
                      <label className="privacy-toggle">
                        <input type="checkbox" name="isPrivate" checked={formData.isPrivate} onChange={handleInputChange} />
                        <span className="toggle-track"><span className="toggle-thumb" /></span>
                        <div className="toggle-content">
                          <span className="toggle-title">{formData.isPrivate ? '🔒 ' + t('createEvent.privateEvent') : '🌐 ' + t('createEvent.publicEvent')}</span>
                          <span className="toggle-desc">{formData.isPrivate ? t('createEvent.privateDescription') : t('createEvent.publicDescription')}</span>
                        </div>
                      </label>
                    </div>
                    {formData.isPrivate && (
                      <motion.div className="form-group access-code-group" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.2 }}>
                        <label>{t('createEvent.accessCode')} *</label>
                        <input type="text" name="accessCode" value={formData.accessCode} onChange={handleInputChange} placeholder={t('createEvent.accessCodePlaceholder')} maxLength={20} className="form-input access-code-input" />
                        <span className="form-hint">{t('createEvent.shareCodeHint')}</span>
                      </motion.div>
                    )}
                  </>
                )}
                {isDemonstrations && (
                  <div className="form-section">
                    <div className="demo-public-note">
                      🌐 <strong>{t('demonstrations.alwaysPublic')}</strong> — {t('demonstrations.publicNote')}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {error && <div className="form-error">⚠️ {error}</div>}

            <div className="step-actions">
              {step > 1 && (
                <button type="button" onClick={goBack} className="btn-secondary step-back">
                  ← {t('common.back')}
                </button>
              )}
              {step < 3 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (!canGoNext()) {
                      setError(t('createEvent.fillAllFields'));
                      return;
                    }
                    goNext();
                  }}
                  className="btn-primary"
                >
                  {t('common.next')} →
                </button>
              ) : (
                <button type="submit" className="btn-primary submit-btn" disabled={loading}>
                  {loading ? t('createEvent.creating') : '✨ ' + (isDemonstrations ? t('demonstrations.createButton') : t('createEvent.createEventButton'))}
                </button>
              )}
            </div>
          </form>

          <div className="back-link">
            <button onClick={() => navigate(isDemonstrations ? '/dems' : '/')}>
              ← {isDemonstrations ? t('demonstrations.backToHome') : t('createEvent.backToHome')}
            </button>
          </div>
        </motion.div>
          </div>
        </div>
      </div>
      
      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>{t('createEvent.creatingEvent')}</p>
        </div>
      )}
    </div>
  );
}

export default CreateEvent;
